<template>
    <div class="share-bar">
        <button type="button" class="share-bar__btn" :disabled="busy" @click="make('result')">
            <v-icon small>mdi-scoreboard-outline</v-icon>Εικόνα αποτελέσματος
        </button>
        <button v-if="mvp" type="button" class="share-bar__btn" :disabled="busy" @click="make('mvp')">
            <v-icon small>mdi-star-circle-outline</v-icon>Εικόνα MVP
        </button>
        <button type="button" class="share-bar__btn" @click="sheetOpen = true">
            <v-icon small>mdi-clipboard-text-outline</v-icon>Φύλλο αγώνα
        </button>
        <match-sheet v-model="sheetOpen" :match-id="matchId" :match="match" :logs="logs" />

        <v-dialog v-model="dialog" max-width="520" content-class="share-dialog-wrap">
            <div class="share-dialog">
                <div class="share-dialog__head">
                    <span>{{ kind === 'mvp' ? 'MVP αγώνα' : 'Αποτέλεσμα' }}</span>
                    <v-btn icon small dark @click="dialog = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
                <div class="share-dialog__preview">
                    <v-progress-circular v-if="busy" indeterminate color="#03e5b7" />
                    <div v-else-if="error" class="share-dialog__error">{{ error }}</div>
                    <img v-else-if="imageUrl" :src="imageUrl" alt="">
                </div>
                <div v-if="file" class="share-dialog__actions">
                    <button v-if="canShareFiles" type="button" class="share-dialog__main" @click="share">
                        <v-icon small>mdi-share-variant</v-icon>Κοινοποίηση
                    </button>
                    <button type="button" class="share-dialog__secondary" @click="download">
                        <v-icon small>mdi-download</v-icon>Λήψη εικόνας
                    </button>
                </div>
                <div v-if="file" class="share-dialog__links">
                    <span>Ή μοιράσου τον σύνδεσμο του αγώνα:</span>
                    <a :href="links.facebook" target="_blank" rel="noopener" aria-label="Facebook"><v-icon>mdi-facebook</v-icon></a>
                    <a :href="links.x" target="_blank" rel="noopener" aria-label="X"><v-icon>mdi-twitter</v-icon></a>
                    <a :href="links.whatsapp" target="_blank" rel="noopener" aria-label="WhatsApp"><v-icon>mdi-whatsapp</v-icon></a>
                </div>
                <p v-if="file" class="share-dialog__hint">
                    {{ canShareFiles
                        ? 'Η «Κοινοποίηση» στέλνει την εικόνα σε Instagram, Facebook, Viber κ.ά.'
                        : 'Κατέβασε την εικόνα και ανέβασέ τη στο Instagram ή στο Facebook.' }}
                </p>
            </div>
        </v-dialog>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import MatchSheet from '@/components/MatchSheet'
    import { saveFile, isNativeApp } from '@/utils/saveFile'
    import { renderResultImage, renderMvpImage, toDataUrl, storageUrl } from '@/utils/matchImage'

    const THEME = {
        background: '#1b1b1b',
        glow: 'rgba(3, 229, 183, 0.24)',
        gradient: ['#03e5b7', '#037ade'],
        accent: '#03e5b7',
        onAccent: '#1b1b1b',
        text: '#ffffff',
        muted: 'rgba(255, 255, 255, 0.62)',
        card: 'rgba(255, 255, 255, 0.08)',
        // Squada One has no Greek letters: the numbers only
        titleFont: "'Roboto Condensed'",
        textFont: 'Roboto',
        numberFont: "'Squada One'",
        logo: require('@/assets/commercial-league-inline-logo.png'),
        logoHeight: 80
    }
    const PHASES = { regular_season: 'Regular Season', playoffs: 'Play Offs' }

    const num = value => Number(value) || 0
    const getStats = player => (player && Array.isArray(player.stats) && player.stats[0]) || null
    // the efficiency of a line, as the league counts it (the MVP of the page of the match)
    function efficiency(s) {
        return (num(s.points) + num(s.rebounds) + num(s.assists) + num(s.steals) + num(s.blockedShots)) -
            ((num(s.twoPointAttempts) + num(s.threePointAttempts) - num(s.twoPointsMade) - num(s.threePointsMade)) +
                (num(s.freeThrowAttempts) - num(s.freeThrowsMade)) + num(s.turnovers) + num(s.personalFouls) + num(s.technicalFouls))
    }
    const teamColor = team => (team && team.color) || THEME.accent

    // The pictures of a played match for the socials: its result, and its MVP (the best efficiency of the winners)
    export default {
        components: { MatchSheet },
        props: {
            match: {
                type: Object,
                required: true
            },
            matchId: {
                type: [Number, String],
                default: null
            },
            // the play-by-play, for the team fouls of the scoresheet
            logs: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                dialog: false,
                sheetOpen: false,
                kind: null,
                busy: false,
                error: null,
                file: null,
                imageUrl: null,
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            theme() {
                return { ...THEME, site: window.location.host }
            },
            score1() {
                return Number(this.match.team1Score) || 0
            },
            score2() {
                return Number(this.match.team2Score) || 0
            },
            title() {
                if (this.match.cup) return (this.match.cupLeague && this.match.cupLeague.title) || 'Cup'
                return [this.match.league && String(this.match.league.title || '').trim(), PHASES[this.match.phase]].filter(Boolean).join(' · ')
            },
            subtitle() {
                const date = this.match.matchDatetime ? momentTimezone(this.match.matchDatetime).tz('Europe/Athens').format('DD/MM/YYYY') : null
                return [this.match.round ? `Αγωνιστική ${this.match.round}` : null, date].filter(Boolean).join(' · ')
            },
            resultText() {
                return `${this.match.team1.name} ${this.score1} - ${this.score2} ${this.match.team2.name}`
            },
            // the best efficiency of the winning team, as on the page of the match
            mvp() {
                if (this.score1 === this.score2) return null
                const key = this.score1 > this.score2 ? 'team1' : 'team2'
                const team = this.match[key] || {}
                const best = (team.players || []).filter(getStats)
                    .map(player => ({ player, stats: getStats(player), pir: efficiency(getStats(player)) }))
                    .reduce((top, item) => (!top || item.pir > top.pir ? item : top), null)
                if (!best || best.pir <= 0) return null
                const stats = best.stats
                return {
                    name: [best.player.name, best.player.surname].map(part => String(part || '').trim()).filter(Boolean).join(' '),
                    photo: best.player.image ? storageUrl(best.player.image) : this.defaultPlayerImg,
                    team: { name: team.name, logo: storageUrl(team.logo), color: teamColor(team) },
                    pir: best.pir,
                    pirLabel: 'EFF',
                    line: [
                        { label: 'ΠΟΝΤΟΙ', value: Number(stats.points) || 0 },
                        { label: 'ΡΙΜΠ.', value: Number(stats.rebounds) || 0 },
                        { label: 'ΑΣΙΣΤ', value: Number(stats.assists) || 0 },
                        { label: 'ΚΛΕΨ.', value: Number(stats.steals) || 0 },
                        { label: 'ΚΟΨ.', value: Number(stats.blockedShots) || 0 }
                    ],
                    result: this.resultText
                }
            },
            canShareFiles() {
                return !!(this.file && navigator.canShare && navigator.canShare({ files: [this.file] }))
            },
            links() {
                const url = window.location.href.split('#')[0]
                const text = `${this.resultText} · ${this.title}`
                return {
                    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
                    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
                    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`
                }
            }
        },
        methods: {
            clearImage() {
                this.imageUrl = null
                this.file = null
            },
            // the points of every period, summed from the players
            periods() {
                const sum = team => {
                    const points = {}
                    ;((team && team.players) || []).forEach(player => {
                        const periodScore = (getStats(player) || {}).periodScore || {}
                        Object.entries(periodScore).forEach(([period, value]) => {
                            points[period] = (points[period] || 0) + (Number(value) || 0)
                        })
                    })
                    return points
                }
                const team1 = sum(this.match.team1)
                const team2 = sum(this.match.team2)
                const keys = [...new Set(Object.keys(team1).concat(Object.keys(team2)))].map(Number).filter(period => Number.isFinite(period) && period >= 1).sort((a, b) => a - b)
                return keys.map(period => ({
                    label: period <= 4 ? `${period}η` : `Παρ. ${period - 4}`,
                    team1: team1[period] === undefined ? '-' : team1[period],
                    team2: team2[period] === undefined ? '-' : team2[period]
                }))
            },
            async make(kind) {
                this.kind = kind
                this.dialog = true
                this.busy = true
                this.error = null
                this.clearImage()
                try {
                    const team = (side, score) => ({ name: side.name, logo: storageUrl(side.logo), color: teamColor(side), score })
                    const blob = kind === 'mvp'
                        ? await renderMvpImage(this.mvp, this.theme)
                        : await renderResultImage({
                            title: this.title,
                            subtitle: this.subtitle,
                            team1: team(this.match.team1, this.score1),
                            team2: team(this.match.team2, this.score2),
                            periods: this.periods()
                        }, this.theme)
                    const name = `${this.match.team1.name}-${this.match.team2.name}${kind === 'mvp' ? '-mvp' : ''}`.replace(/[\s/\:*?"<>|]+/g, '-')
                    this.file = new File([blob], `${name}.png`, { type: 'image/png' })
                    this.imageUrl = await toDataUrl(blob)
                } catch (error) {
                    this.error = 'Η εικόνα δεν μπόρεσε να δημιουργηθεί.'
                } finally {
                    this.busy = false
                }
            },
            share() {
                // in the app the share sheet of the phone, which is more reliable than the one of the web view
                if (isNativeApp()) return this.download()
                navigator.share({ files: [this.file], title: this.resultText, text: `${this.resultText} · ${this.title}` }).catch(() => {})
            },
            download() {
                saveFile(this.file, this.file.name, this.kind === 'mvp' ? 'MVP αγώνα' : this.resultText).catch(() => {
                    this.error = 'Η εικόνα δεν μπόρεσε να αποθηκευτεί.'
                })
            }
        }
    }
</script>

<style scoped>
.share-bar {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;
    margin: 0 0 16px;
}

.share-bar__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: 1px solid rgba(3, 229, 183, 0.5);
    border-radius: 999px;
    background: #232323;
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
}

.share-bar__btn .v-icon {
    color: #03e5b7 !important;
}

.share-bar__btn:hover:not(:disabled) {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #1b1b1b;
}

.share-bar__btn:hover:not(:disabled) .v-icon {
    color: #1b1b1b !important;
}

.share-bar__btn:disabled {
    opacity: 0.6;
    cursor: default;
}

.share-dialog {
    padding: 16px;
    border-radius: 16px;
    background: #232323;
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
}

.share-dialog__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    font-size: 1.1rem;
    font-weight: 800;
}

.share-dialog__preview {
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 1080 / 1350;
    border-radius: 12px;
    background: #1b1b1b;
    overflow: hidden;
}

.share-dialog__preview img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.share-dialog__error {
    padding: 24px;
    color: rgba(255, 255, 255, 0.75);
    text-align: center;
}

.share-dialog__actions {
    display: flex;
    gap: 10px;
    margin-top: 14px;
}

.share-dialog__main,
.share-dialog__secondary {
    display: inline-flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 14px;
    border-radius: 10px;
    font-weight: 700;
    cursor: pointer;
}

.share-dialog__main {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #1b1b1b;
}

.share-dialog__main .v-icon {
    color: #1b1b1b !important;
}

.share-dialog__secondary {
    border: 1px solid rgba(255, 255, 255, 0.25);
    color: #fff;
}

.share-dialog__secondary .v-icon {
    color: #fff !important;
}

.share-dialog__links {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
}

.share-dialog__links a .v-icon {
    color: #fff !important;
}

.share-dialog__links a:hover .v-icon {
    color: #03e5b7 !important;
}

.share-dialog__hint {
    margin: 10px 0 0;
    color: rgba(255, 255, 255, 0.55);
    font-size: 0.8rem;
}
</style>
